import { createSlice } from "@reduxjs/toolkit"
import { persistReducer } from "redux-persist"
import storage from "redux-persist/lib/storage"

import { COMPARE_IDS } from "../../../Components/Filters/CompareSelect/presets"
import { frozenStateReconciler } from "../../../Utils/persistHelpers"
import { logout } from "../../LoginPage/redux/auth.actions"
import { ADMIN_DEFLECT_ANALYTICS_SLICE_NAME as SLICE_NAME } from "./adminDeflectAnalytics.constants"
import { CARD_THUNKS } from "./adminDeflectAnalytics.thunks"

/* ------------- Initial State ------------- */
// What the page opens on (DAA-2-08 … DAA-2-17). User email and Test On / Test Off are the admin
// panel's global filters (globalFilters): `user_email` here is the copy DropdownBlockTags keeps in
// step with them, and Test On / Test Off is read from there.
export const DEFAULT_ADMIN_DEFLECT_FILTERS = {
  program: "all",
  channels: "all",
  user_email: [],
  user_full_name: [],
  business_account: [],
  // Keys of the tag filters turned into an exclusion ("Hide filtered accounts/alerts").
  exclude_fields: [],
  // "Last 4 weeks"
  period: "27 days",
  compare: { mode: COMPARE_IDS.PREV_PERIOD },
  lastCompare: null
}

// Card responses by card key (CARD_THUNKS). `requestIds` holds each card's latest request: an
// older response that arrives after a newer filter change is dropped.
const initialState = {
  filters: { ...DEFAULT_ADMIN_DEFLECT_FILTERS },
  cards: {},
  fetching: {},
  requestIds: {}
}

/* ------------- Slice ------------- */
const slice = createSlice({
  name: SLICE_NAME,
  initialState,
  reducers: {
    changeAdminDeflectFilters(state, { payload }) {
      state.filters = { ...state.filters, ...(payload ?? {}) }
    }
  },
  extraReducers: (builder) => {
    builder.addCase(logout, () => initialState)

    Object.entries(CARD_THUNKS).forEach(([key, thunk]) => {
      builder
        .addCase(thunk.pending, (state, { meta }) => {
          state.fetching[key] = true
          state.requestIds[key] = meta.requestId
        })
        .addCase(thunk.fulfilled, (state, { payload, meta }) => {
          if (state.requestIds[key] !== meta.requestId) return

          state.cards[key] = payload
          state.fetching[key] = false
        })
        // The previous data stays under the card (DAA-1-17).
        .addCase(thunk.rejected, (state, { meta }) => {
          if (state.requestIds[key] === meta.requestId) state.fetching[key] = false
        })
    })
  }
})

export const { changeAdminDeflectFilters } = slice.actions

/* ------------- Selectors ------------- */
export const selectAdminDeflectFilters = (state) => state[SLICE_NAME].filters
export const selectAdminDeflectCard = (key) => (state) => state[SLICE_NAME].cards[key]
export const selectAdminDeflectCardFetching = (key) => (state) =>
  Boolean(state[SLICE_NAME].fetching[key])

/* ------------- Persistence ------------- */
// The filters survive a visit (DAA-1-16).
const PERSISTED_REDUCER_VERSION = 1

export const persistedReducer = persistReducer(
  {
    key: SLICE_NAME,
    storage,
    version: PERSISTED_REDUCER_VERSION,
    whitelist: ["filters"],
    stateReconciler: frozenStateReconciler,
    migrate: async (state) => {
      if (state?._persist?.version < PERSISTED_REDUCER_VERSION) return undefined
      return state
    }
  },
  slice.reducer
)

export default slice.reducer
